import { Injectable, computed, inject, signal } from '@angular/core';
import { BehaviorSubject, Subject, map, shareReplay } from 'rxjs';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { KundeTokenService, TokenService } from '@comparit/infrastructure';
import { ResultsHubEvents } from '@comparit/core';
import { ErgebnisIdentifier, Sparte } from '@comparit/common-api';
import { BASE_PATH, KfzBerechnungResponse, KfzErgebnis } from '../interfaces/generated';
import { toSignal } from '@angular/core/rxjs-interop';

class ResultsHelper {
  private readonly apiUrl = inject(BASE_PATH);
  private readonly vermitlerToken = inject(TokenService);
  private readonly kundeToken = inject(KundeTokenService);
  private connection: HubConnection | null = null;

  public allResults$ = new BehaviorSubject<KfzErgebnis[]>([]);
  all = toSignal(this.allResults$, { initialValue: [] });
  public berechnungResponse = signal<KfzBerechnungResponse | null>(null);
  public currentNumberOfErgebnisse = computed(() => this.all()?.length ?? 0);
  public hasError = signal<boolean>(false);
  public noResults = signal<boolean>(false);

  public async start(request: unknown): Promise<void> {
    await this.closeConnectionDirect(this.connection);
    this.connection = this.createConnection();

    if (!this.connection) return;
    this.resetData();

    this.registerEventHandlers(this.connection);

    await this.startConnection(this.connection);
    await this.sendRequest(this.connection, request);
  }

  private createConnection(): HubConnection | null {
    try {
      return new HubConnectionBuilder()
        .withUrl(`${this.apiUrl}/berechnung`, {
          // TODO change later
          withCredentials: false,
        })
        .build();
    } catch (e) {
      console.log(e);
      return null;
    }
  }

  public ergebnisReceived = new Subject<KfzErgebnis[]>();
  private registerEventHandlers(connection: HubConnection): void {
    connection.on(ResultsHubEvents.ReceiveVersichererResponse, (data: KfzErgebnis[]) => {
      console.log(data);
      const current = this.allResults$.getValue();
      this.allResults$.next([...current, ...data]);
      this.ergebnisReceived.next(data);
    });

    connection.on(ResultsHubEvents.ReceiveBerechnungResponse, (data: KfzBerechnungResponse) => {
      this.berechnungResponse.set(data);
    });
  }

  private async startConnection(connection: HubConnection): Promise<void> {
    if (connection.state !== HubConnectionState.Disconnected) {
      console.warn('Connection already started or in progress:', connection.state);
      return;
    }

    try {
      await connection.start();
      console.log('Connection started.');
    } catch (error) {
      console.error('Error starting connection:', error);
    }
  }

  private async sendRequest(connection: HubConnection, request: unknown): Promise<void> {
    try {
      const res = await connection.invoke(
        'SendBerechnungRequestWithKundeToken',
        Sparte.Kfz,
        request,
        this.vermitlerToken.getToken(),
        this.kundeToken.getToken()
      );
      if (!res.error) {
        if (this.allResults$.getValue().length === 0) {
          this.noResults.set(true);
        }

        const berechnungResponse = this.berechnungResponse();
        if (berechnungResponse) {
          this.berechnungResponse.set({ ...berechnungResponse, anzahlTarife: this.all().length });
        }
        connection.stop();
      } else {
        this.hasError.set(true);
      }
    } catch (error) {
      this.hasError.set(true);
      connection.stop();
      console.error('Error sending request:', error);
    }
  }

  async closeConnectionDirect(hubConnection: HubConnection | null) {
    if (hubConnection && hubConnection.state === HubConnectionState.Connected) {
      try {
        await hubConnection.stop();
        console.log('Connection closed Direct gracefully.');
      } catch (err) {
        console.error('Error when stopping the connection:', err);
      }
    }
  }

  private resetData(): void {
    this.noResults.set(false);
    this.hasError.set(false);
    this.allResults$.next([]);
    this.berechnungResponse.set(null);
  }
}

@Injectable({ providedIn: 'root' })
export class ResultsService extends ResultsHelper {
  /**
   * Searches only the calculation results (SignalR flow).
   * Prefer TarifLookupService.findTarif$ which also checks Angebot data.
   */
  findTarif$ = (identifier: ErgebnisIdentifier) => {
    return this.allResults$.pipe(
      map(ergebnise =>
        ergebnise.find(
          item =>
            item.kombinationId === identifier.kombinationId &&
            item.berechnungId === identifier.berechnungId
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  };
}
